import React from 'react';
import { Link } from 'react-router-dom';
import CenterModalWindow from '../../layout/ReusableComponent/CenterModalWindow/CenterModalWindow';
import '../../layout/ReusableComponent/CenterModalWindow/CenterModalWindow.css';
import { APP_NAME } from '../../config/branding';
import { STORAGE_BOX_BIN_RETENTION_DAYS } from '../constants';

function PolicyLink({ to, onClose, children }) {
  return (
    <Link to={to} className="sb-storage-terms-modal__nav-link" onClick={onClose}>
      {children}
    </Link>
  );
}

export default function StorageBoxStorageTermsModal({ isOpen, onClose }) {
  return (
    <CenterModalWindow
      isOpen={isOpen}
      onClose={onClose}
      width="min(540px, calc(100vw - 28px))"
      height="min(82dvh, 680px)"
      contentClassName="sb-storage-terms-modal"
      contentStyle={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
    >
      <div className="sb-storage-terms-modal__inner" data-modal-scroll-port>
        <header className="sb-storage-terms-modal__head">
          <h2 id="sb-storage-terms-title">Storage terms &amp; help</h2>
          <p className="sb-storage-terms-modal__lead">
            The short version of how Storage Box treats your files — plus where to go when you need policies, exports, or
            to flag something that should not be here.
          </p>
        </header>

        <nav className="sb-storage-terms-modal__nav" aria-label="Storage policies and help">
          <p className="sb-storage-terms-modal__nav-title">Jump to</p>
          <ul className="sb-storage-terms-modal__nav-list">
            <li>
              <PolicyLink to="/terms" onClose={onClose}>
                {APP_NAME} terms of service
              </PolicyLink>
            </li>
            <li>
              <PolicyLink to="/legal/acceptable-use" onClose={onClose}>
                Acceptable use &amp; how we enforce it
              </PolicyLink>
            </li>
            <li>
              <PolicyLink to="/legal/safety" onClose={onClose}>
                Report a violation or safety concern
              </PolicyLink>
            </li>
            <li>
              <PolicyLink to="/contact" onClose={onClose}>
                Ask for a review of a restriction or decision
              </PolicyLink>
            </li>
            <li>
              <PolicyLink to="/privacy" onClose={onClose}>
                Privacy &amp; related policies
              </PolicyLink>
            </li>
            <li>
              <a href="#sb-storage-export" className="sb-storage-terms-modal__nav-link">
                Exporting your Storage Box data
              </a>
            </li>
            <li>
              <a href="#sb-storage-sharing" className="sb-storage-terms-modal__nav-link">
                Sharing, access &amp; cutting people off
              </a>
            </li>
            <li>
              <PolicyLink to="/legal/fair-use" onClose={onClose}>
                Educational &amp; creative use exceptions
              </PolicyLink>
            </li>
            <li>
              <a href="#sb-storage-features" className="sb-storage-terms-modal__nav-link">
                Search, tags &amp; optional smart features
              </a>
            </li>
            <li>
              <a href="#sb-storage-security" className="sb-storage-terms-modal__nav-link">
                Malware, abuse &amp; keeping accounts safe
              </a>
            </li>
            <li>
              <a href="#sb-storage-sensitive" className="sb-storage-terms-modal__nav-link">
                Intimate or explicit material
              </a>
            </li>
          </ul>
        </nav>

        <section className="sb-storage-terms-modal__section">
          <h3>Your content stays yours</h3>
          <p>
            Uploads belong to you. We are not trying to own your photos, spreadsheets, or folder names. To run the
            service we do need a limited licence to host copies, show them back to you, build thumbnails where we can,
            and carry out the moves, renames, and shares you ask for. You keep the rights; we keep the lights on.
          </p>
        </section>

        <section className="sb-storage-terms-modal__section">
          <h3>Private until you say otherwise</h3>
          <p>
            Storage in your account is private by default. If your organisation uses {APP_NAME} for work or study, your
            admin may set extra rules — but we will not flip a personal folder into something public without you doing
            it on purpose.
          </p>
          <p>
            When you share a link or invite a colleague, you choose who gets in. Revoke access anytime; treat shared
            links like spare keys. You are also responsible for having permission to share what you upload.
          </p>
        </section>

        <section className="sb-storage-terms-modal__section" id="sb-storage-sharing">
          <h3>Sharing, access &amp; cutting people off</h3>
          <p>
            Storage Box sharing is deliberate: named users, link access, or Lock folder temp PINs — each with its own
            expiry and revoke path in the details panel. There is no universal &quot;block in Drive&quot; button here;
            instead, stop sharing, remove grantees, or delete the link. If someone is harassing you on the wider platform,
            use profile and social controls there, and still{' '}
            <Link to="/legal/safety" className="sb-storage-terms-modal__link" onClick={onClose}>
              report
            </Link>{' '}
            if files or messages cross the line.
          </p>
        </section>

        <section className="sb-storage-terms-modal__section">
          <h3>What we do (and do not do) with your files</h3>
          <p>
            We process stored data to deliver Storage Box: capacity limits, search, favourites, colour tags, the Bin, and
            sync between devices. We do not mine your private files for ad targeting or unrelated marketing.
          </p>
          <p>
            Content that appears elsewhere on {APP_NAME} — posts, comments, support threads — still follows community and
            moderation rules. Storage Box is a quiet corner, not a free pass to host anything illegal or harmful.
          </p>
        </section>

        <section className="sb-storage-terms-modal__section" id="sb-storage-features">
          <h3>Search, tags &amp; optional smart features</h3>
          <p>
            Features like search, labels, colour tags, and previews run on metadata we already store (names, types, dates)
            so you can find things faster. They are part of the product, not a separate ad network. If we ever add
            optional assistants that read file names or content to help you organise, we will describe what leaves your
            device and how to turn it off — plain language, no buried toggles.
          </p>
        </section>

        <section className="sb-storage-terms-modal__section" id="sb-storage-security">
          <h3>Malware, abuse &amp; keeping accounts safe</h3>
          <p>
            Do not use Storage Box to distribute viruses, credential stealers, or bulk unsolicited files. We may scan or
            block uploads that match known abuse patterns, throttle accounts that hammer the API, and cooperate with
            moderation when storage is tied to spam or scams on the rest of {APP_NAME}. Serious or repeated abuse can mean
            losing upload access or the account entirely, per our{' '}
            <Link to="/legal/acceptable-use" className="sb-storage-terms-modal__link" onClose={onClose}>
              Acceptable Use
            </Link>{' '}
            rules.
          </p>
        </section>

        <section className="sb-storage-terms-modal__section" id="sb-storage-sensitive">
          <h3>Intimate or explicit material</h3>
          <p>
            Non-consensual intimate imagery, exploitation, or content involving minors has no place on {APP_NAME}. If you
            find it in storage you can access — or someone shared it with you — report through our{' '}
            <Link to="/legal/safety" className="sb-storage-terms-modal__link" onClose={onClose}>
              Safety &amp; Reporting
            </Link>{' '}
            page or{' '}
            <Link to="/contact" className="sb-storage-terms-modal__link" onClick={onClose}>
              Contact
            </Link>{' '}
            form with links and context. We treat those reports urgently even when the file itself is not public on the
            feed.
          </p>
        </section>

        <section className="sb-storage-terms-modal__section">
          <h3>Deletes and the Bin</h3>
          <p>
            Deleting usually sends items to the Bin first. They linger for about {STORAGE_BOX_BIN_RETENTION_DAYS} days so
            you can restore them. After that, or if you empty the Bin, we treat removal as permanent for everyday use.
            Disaster-recovery backups or legal holds may retain copies for a short period — see the{' '}
            <Link to="/privacy" className="sb-storage-terms-modal__link" onClick={onClose}>
              Privacy Policy
            </Link>
            .
          </p>
        </section>

        <section className="sb-storage-terms-modal__section">
          <h3>Lock folder</h3>
          <p>
            Lock folder asks for a PIN and encrypts file data before it lands in cloud storage. That adds a real barrier
            if someone only steals raw blobs — but it is not identical to full end-to-end encryption where the service
            never sees your keys. Pick a strong PIN, do not share it, and use Lock folder for sensitive material you
            understand the limits of.
          </p>
        </section>

        <section className="sb-storage-terms-modal__section" id="sb-storage-export">
          <h3>Exporting your Storage Box data</h3>
          <p>
            You can pull your files out the same way you put them in: download items from the details panel, grab folders
            file by file, and copy anything shared with you before access ends. There is no single &quot;export entire
            drive&quot; wizard yet — if you are closing your account, work through top-level folders so nothing important
            stays behind. Tenant admins may have separate backup duties; everyone else should plan ahead.
          </p>
        </section>

        <section className="sb-storage-terms-modal__section">
          <h3>Limits and leaving</h3>
          <p>
            Accounts and tenants may have storage caps. Automated abuse, malware, or attempts to dodge quotas can mean
            throttling or suspension. Before you leave {APP_NAME}, export what you need and empty shared links you no
            longer want live.
          </p>
        </section>

        <section className="sb-storage-terms-modal__section">
          <h3>When enforcement hits your storage</h3>
          <p>
            If we remove or restrict content, it is usually because of a policy breach, a valid legal request, or a
            security risk — not because we disagree with your opinions. Think you were flagged by mistake? Use{' '}
            <Link to="/contact" className="sb-storage-terms-modal__link" onClick={onClose}>
              Contact
            </Link>{' '}
            with your account email, what was removed, and why you believe it should be restored. We read those messages;
            we cannot always put everything back, but we do review appeals in good faith.
          </p>
        </section>

        <footer className="sb-storage-terms-modal__footer">
          <p>
            This guide is here to orient you, not replace the contract. Binding text lives in our{' '}
            <Link to="/terms" className="sb-storage-terms-modal__link" onClick={onClose}>
              Terms &amp; Conditions
            </Link>
            ,{' '}
            <Link to="/privacy" className="sb-storage-terms-modal__link" onClick={onClose}>
              Privacy Policy
            </Link>
            ,{' '}
            <Link to="/legal/code-of-conduct" className="sb-storage-terms-modal__link" onClick={onClose}>
              Code of Conduct
            </Link>
            , and{' '}
            <Link to="/legal/acceptable-use" className="sb-storage-terms-modal__link" onClick={onClose}>
              Acceptable Use
            </Link>
            .
          </p>
        </footer>
      </div>
    </CenterModalWindow>
  );
}
